import { forwardRef, useLayoutEffect, useRef, useState } from "react";

// Campo numerico per quantità/giacenze. type="number" in Firefox/Safari restituisce "" per "2," / "2."
// mentre si digita (e molti onChange fanno +v || 0) -> il campo si azzerava. Qui il campo è testo:
// accetta "," e ".", mostra esattamente quanto digitato e passa al genitore il valore con il punto.
// I prezzi stile cassa (data-euro) restano type=number con la logica di NumberInputFlow.
const clean = (s) => {
  let out = "", sep = false;
  for (const ch of String(s)) {
    if (ch >= "0" && ch <= "9") out += ch;
    else if ((ch === "," || ch === ".") && !sep) { sep = true; out += ch; }
    else if (ch === "-" && out === "") out += ch;
  }
  return out;
};
// "2,5" -> "2.5", "2," -> "2", ",5" -> "0.5"; null se ancora incompleto ("-", ",").
const toNum = (s) => {
  let v = s.replace(",", ".");
  if (v.endsWith(".")) v = v.slice(0, -1);
  if (v.startsWith(".")) v = "0" + v;
  else if (v.startsWith("-.")) v = "-0" + v.slice(1);
  if (v === "") return "";
  return /^-?\d+(\.\d+)?$/.test(v) ? v : null;
};
const str = (v) => (v == null ? "" : String(v));

const withValue = (e, value) => {
  const t = e.target;
  const target = new Proxy(t, {
    get: (o, k) => {
      if (k === "value") return value;
      if (k === "valueAsNumber") return value === "" ? NaN : +value;
      const r = Reflect.get(o, k);
      return typeof r === "function" ? r.bind(o) : r;
    },
  });
  return Object.create(e, { target: { value: target }, currentTarget: { value: target } });
};

const NumInput = forwardRef(function NumInput({ type, value, onChange, onBlur, ...props }, ref) {
  const [draft, setDraft] = useState(null);
  const sent = useRef(null);
  const ours = useRef(false);
  const controlled = value !== undefined;

  // Il genitore ha cambiato il valore per conto suo (reset dopo "aggiungi", ricarica) -> si adotta.
  useLayoutEffect(() => {
    if (ours.current) { ours.current = false; return; }
    if (draft !== null) setDraft(null);
  }, [value]); // eslint-disable-line

  if (props["data-euro"] != null) return <input ref={ref} type="number" value={value} onChange={onChange} onBlur={onBlur} {...props} />;

  const emit = (e, v) => {
    if (v === sent.current) return;
    sent.current = v;
    ours.current = true;
    setTimeout(() => { ours.current = false; }, 0);
    onChange?.(withValue(e, v));
  };

  const handleChange = (e) => {
    const raw = clean(e.target.value);
    if (!controlled) {
      const el = e.target;
      const norm = raw.replace(",", ".");
      if (el.value !== norm) {
        const pos = Math.max(0, (el.selectionStart ?? norm.length) - (el.value.length - norm.length));
        el.value = norm;
        try { el.setSelectionRange(pos, pos); } catch {}
      }
      onChange?.(e);
      return;
    }
    if (draft === null) sent.current = str(value);
    setDraft(raw);
    const n = toNum(raw);
    if (n !== null) emit(e, n);
  };

  const handleBlur = (e) => {
    if (controlled && draft !== null) {
      const n = toNum(draft);
      if (n !== null) emit(e, n);
      setDraft(null);
    }
    onBlur?.(e);
  };

  return (
    <input ref={ref} type="text" inputMode="decimal" autoComplete="off" data-num=""
      {...props}
      {...(controlled ? { value: draft ?? str(value) } : {})}
      onChange={handleChange} onBlur={handleBlur} />
  );
});

export default NumInput;
